import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Progress } from 'foundations-components/transitional/components/Progress';
import UsageTracker from '../../../utils/UsageTracker';
import QuizMeAnswers from '../../interactiveEducation/components/QuizMeAnswers.migrated';
import CompanionFooter from '../../interactiveEducation/components/CompanionFooter.migrated';
import { useLessonPreQuizSession } from '../hooks/useLessonPreQuiz.migrated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const LessonPreQuizQuestionsContainer = ({
  interactionProps,
  lessonId,
  onSkip,
  onTimestampClick,
  progressProps,
  questions
}) => {
  var _quizState$questionIn;
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState();
  const [selectedAnswerCorrect, setSelectedAnswerCorrect] = useState();
  const {
    getLessonPreQuizState,
    incrementLessonPreQuizQuestion,
    confirmQuestionReviewed
  } = useLessonPreQuizSession();
  const quizState = getLessonPreQuizState(lessonId);
  const rawQuestionIndex = (_quizState$questionIn = quizState === null || quizState === void 0 ? void 0 : quizState.questionIndex) !== null && _quizState$questionIn !== void 0 ? _quizState$questionIn : 0;
  const questionIndex = rawQuestionIndex ? questionAnswered ? rawQuestionIndex - 1 : rawQuestionIndex : 0;
  const quizLength = questions.length;
  const currentQuestion = questions[questionIndex];
  const isLastQuestion = rawQuestionIndex >= quizLength;
  const displayQuestionIndex = questionIndex + 1;
  const percentComplete = quizLength > 0 ? Math.round(rawQuestionIndex / quizLength * 100) : 0;
  useEffect(() => {
    /**
     * If the page is rendered past the index of the last question, we auto confirm that the user has reviewed the answer
     * This will advance the page to the completion screen
     */
    if (!currentQuestion && quizState !== null && quizState !== void 0 && quizState.isReviewingAnswer) {
      confirmQuestionReviewed(lessonId);
    }
  }, [currentQuestion, quizState === null || quizState === void 0 ? void 0 : quizState.isReviewingAnswer, lessonId, confirmQuestionReviewed]);
  const handleSelectAnswerIndexChange = (answerIndex, correct) => {
    setSelectedAnswerIndex(answerIndex);
    setSelectedAnswerCorrect(correct);
  };
  const handleCheckAnswer = () => {
    if ((currentQuestion === null || currentQuestion === void 0 ? void 0 : currentQuestion.lessonContentId) === undefined) {
      return;
    }
    UsageTracker.trackStandalone('Content Progress', Object.assign({}, progressProps, {
      action: 'ai-pre-assessment-answered-question',
      value: selectedAnswerCorrect ? 'ai-pre-assessment-answer-correct' : 'ai-pre-assessment-answer-incorrect'
    }));
    incrementLessonPreQuizQuestion({
      correct: !!selectedAnswerCorrect,
      lessonId,
      lessonContentId: currentQuestion.lessonContentId
    });
    setQuestionAnswered(true);
  };
  const handleSkip = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, interactionProps, {
      action: 'ai-pre-assessment-skipped-in-progress'
    }));
    onSkip();
  };
  const handleNextQuestion = () => {
    confirmQuestionReviewed(lessonId);
    setQuestionAnswered(false);
    setSelectedAnswerIndex(undefined);
    setSelectedAnswerCorrect(undefined);
  };
  if (!currentQuestion) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    inlineSize: "100%",
    children: [/*#__PURE__*/_jsxs(View, {
      flexGrow: 1,
      paddingBlockStart: "700",
      paddingInline: "700",
      children: [/*#__PURE__*/_jsx(Text, {
        textAlign: "center",
        typography: "heading-400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.title"
        })
      }), /*#__PURE__*/_jsx(Text, {
        marginBlockEnd: "600",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.questionScreen.subtitle"
        })
      }), /*#__PURE__*/_jsx(Progress, {
        "aria-label": I18n.text('learningCenter.preAssessmentQuiz.questionScreen.progressAriaLabel'),
        descriptiveLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.preAssessmentQuiz.questionScreen.questionProgress",
          options: {
            progress: displayQuestionIndex,
            total: quizLength
          }
        }),
        use: "success",
        value: percentComplete,
        valueLabel: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.preAssessmentQuiz.questionScreen.percentComplete",
          options: {
            percentage: /*#__PURE__*/_jsx(FormattedPercentage, {
              value: percentComplete
            })
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        marginBlock: "800",
        textAlign: "center",
        typography: "body-300",
        children: currentQuestion.questionTitle
      }), /*#__PURE__*/_jsx(QuizMeAnswers, {
        handleSelectAnswerIndexChange: handleSelectAnswerIndexChange,
        onTimestampClick: onTimestampClick,
        questionAnswered: questionAnswered,
        questionIndex: questionIndex,
        quizQuestions: questions,
        selectedAnswerIndex: selectedAnswerIndex
      })]
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      additionalButtons: /*#__PURE__*/_jsxs(ButtonWrapper, {
        innerFlexContainerStyles: {
          flexWrap: 'nowrap'
        },
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: handleSkip,
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.questionScreen.skipAssessment"
          })
        }), questionAnswered && /*#__PURE__*/_jsx(Button, {
          onClick: handleNextQuestion,
          use: "primary",
          children: isLastQuestion ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.questionScreen.next"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.questionScreen.nextQuestion"
          })
        }), !questionAnswered && selectedAnswerIndex !== undefined && /*#__PURE__*/_jsx(Button, {
          onClick: handleCheckAnswer,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.preAssessmentQuiz.questionScreen.checkAnswer"
          })
        })]
      }),
      borderBlockStartStyle: "none",
      contentType: "LESSON",
      correlationId: currentQuestion.correlationId,
      featureId: "QUIZ_ME",
      primaryContentId: lessonId,
      trackingAction: "AI_PRE_ASSESSMENT_FEEDBACK"
    })]
  });
};